import React, { useState, useEffect, useContext, useRef, forwardRef, useImperativeHandle } from "react";
import { ReactComponent as DroupUp } from '../../assets/svg/drop_up.svg';
import { ReactComponent as Duplicate} from '../../assets/svg/duplicate.svg';
import { ReactComponent as Plus } from '../../assets/svg/plus.svg';
import { ReactComponent as Delete } from '../../assets/svg/delete_icon.svg';
import { ReactComponent as Menu } from '../../assets/svg/menu.svg';
import { ReactComponent as Edit } from '../../assets/svg/edit.svg'; 
import { Selector } from "../Shared/Selector";
import { Button } from "../Shared/Buttons/Button";
import { Switch } from "../Shared/Buttons/Switch";
import { TextInput } from "../Shared/Inputs/TextInput";
import { 
    SampleQuestion, 
    SMSModelValidation, 
    SMSServiceModel, 
    VacancyEditResponse 
} from "../../models/VacancyPostingModel";
import { PopUpWindow } from '../Shared/PopUpWindow';
import { getPreApplicationSampleQuestions } from "../../services/VacancyService";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedRecruiter } from "../../models/LoggedRecruiter";
import useErrorPage from "../../hooks/useErrorPage";
import { useAlert } from "../../hooks/useAlert";
import { VacancyQuestionLoader } from "../Loaders/VacancyQuestionLoader";
import { CheckboxInput } from "../Shared/Inputs/CheckboxInput";
import { TextAreaInput } from "../Shared/Inputs/TextAreaInput";
import { PreApplicationQuestionModel, PreApplicationQuestionValidation } from "../../models/PreApplicationQuestionModel";
import { validatePhoneNumber } from "../../helpers/Common";
import { SelectorModel } from "../../models/SelectorModel";
import { SummaryOfCompletionModel } from "../../models/SummaryOfCompletionModel";
import { useMediaQuery } from 'react-responsive';
import { InputValidation } from "../../models/InputValidation";

interface Props {  
    styles: any;
    jobId?: number;
    onUpdateSummary: (model?: Partial<SummaryOfCompletionModel>) => void;
    onTabChange: (tab: string) => void;
    onLoad: () => Promise<VacancyEditResponse>;
    onSave: (model: VacancyEditResponse) => void;
}

export interface CVLessQuestionsEditHandle {
    saveTab: () => Promise<{ data: VacancyEditResponse } | null>;
}

const initialValidation: PreApplicationQuestionValidation = {
    question: {},
    answers: {}
}

const initialSMSValidation: SMSModelValidation = {
    message: {}
}

export const CVLessQuestionsEdit = forwardRef<CVLessQuestionsEditHandle, Props>((props, ref) => {
    const jobId = props.jobId;
    const styles = props.styles;      
    const authContext = useContext(AuthContext);    
    const recruiter: LoggedRecruiter = authContext.getLoggedRecruiter();
    const [openFrequentlyQuestions, setOpenFrequentlyQuestions] = useState<boolean>(false);
    const [showAddQuestion, setShowAddQuestion] = useState<boolean>(false);
    const [selectedQuestionType, setSelectedQuestionType] = useState<string>("");
    const [currentAnswer, setCurrentAnswer] = useState<string>("");
    const [currentLocation, setCurrentLocation] = useState<string>("");
    const [contactNumber, setContactNumber] = useState<string>("");
    const [contactNumberValidation, setContactNumberValidation] = useState<InputValidation>({});
    const [questionsList, setQuestionsList] = useState<PreApplicationQuestionModel[]>([]);
    const [sampleQuestions, setSampleQuestions] = useState<SampleQuestion[]>([]);
    const [jobQuestionTypes, setJobQuestionTypes] = useState<SelectorModel[]>([]);
    const [searchQuestions, setSearchQuestions] = useState("");
    const [editQuestionIndex, setEditQuestionIndex] = useState<number>(0);
    const [editEnable, setEditEnable] = useState<boolean>(false);
    const [openSingleDeleteDialog, setOpenSingleDeleteDialog] = useState<boolean>(false);
    const [openBulkDeleteDialog, setOpenBulkDeleteDialog] = useState<boolean>(false);
    const [validation, setValidation] = useState<PreApplicationQuestionValidation>(initialValidation);
    const [smsValidation, setSMSValidation] = useState<SMSModelValidation>(initialSMSValidation);
    const [loading, setLoading] = useState<boolean>(false);
    const topRef = useRef<HTMLDivElement>(null);
    const lastQuestionRef = useRef<HTMLDivElement | null>(null);
    const { Alert, openAlert } = useAlert();
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [allSelected, setAllSelected] = useState<boolean>(false);
    const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
    const [smsServiceEnabled, setSMSServiceEnabled] = useState<boolean>(false);
    const [openSMSServiceDialog, setOpenSMSServiceDialog] = useState<boolean>(false);    
    const [emailServiceEnabled, setEmailServiceEnabled] = useState<boolean>(true);
    const [openEmailServiceDialog, setOpenEmailServiceDialog] = useState<boolean>(false);    
    const [openDefaultQuestionsDialog, setOpenDefaultQuestionsDialog] = useState<boolean>(false);
    const [showTags, setshowTags] = useState<boolean>(false);
    const [interviewDate, setInterviewDate] = useState<string>("");    
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();

    const initialQuestion: PreApplicationQuestionModel = {
        jobQuestionId: 0,
        jobId: jobId, 
        questionType: 0,        
        question: "",
        type: "",        
        answers: [],
        deleted: false
    };

    const initialSMSService: SMSServiceModel = {
        jobId: jobId,
        message: "",
        interviewDates: [],
        branches: [] 
    };

    const [preApplicationQuestion, setPreApplicationQuestion] = useState<PreApplicationQuestionModel>(initialQuestion);
    const [smsService, setSMSService] = useState(initialSMSService);
    const [sampleQuestionIds, setSampleQuestionIds] = useState<number[]>([]);
    const [deletedQuestionIds, setDeletedQuestionIds] = useState<number[]>([]);
    const [updatedQuestionList, setUpdatedQuestionList] = useState<PreApplicationQuestionModel[]>([]);

    useEffect(() => { 
        getCVLessQuestionDetails();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const getCVLessQuestionDetails = async () => {
        const vacancyQuestion = await props.onLoad();
        setSMSService(vacancyQuestion.smsService!);
        setSMSServiceEnabled(vacancyQuestion.smsServiceStatus ?? (!!vacancyQuestion.smsService?.message));
        setQuestionsList(vacancyQuestion?.preApplicationQuestionList ?? []);
        setEmailServiceEnabled(vacancyQuestion?.emailServiceStatus ?? true);
        setJobQuestionTypes(vacancyQuestion?.questionTypes ?? []);
        setUpdatedQuestionList(vacancyQuestion?.updatedPreApplicationQuestionList ?? []);
        setDeletedQuestionIds(vacancyQuestion.deletedQuestionIds ?? []);
    };

    const questionTypeLabels: Record<number, string> = {
        3: "Single Choice",
        5: "Open"
    }

    async function getPreApplicationSampleQuestionsList() {
        setLoading(true);
        await getPreApplicationSampleQuestions()
        .then((questions: SampleQuestion[]) => {    
            setSampleQuestions(questions); 
            setLoading(false);
        })
        .catch((error) => {
            toErrorPage(error);
        });
    }

    const handleSelectItems = (selector: string, selectedItem: SelectorModel) => {
        setPreApplicationQuestion(prev => ({
            ...prev,
            questionType: selectedItem.id ?? 0,
            type: selectedItem.value
        }));
        setSelectedQuestionType(selectedItem.value);
    }

    const handleSMSService = () => {
        setOpenSMSServiceDialog(!openSMSServiceDialog);
    }

    async function updateSMSServiceStatus() {        
        try {            
            setSMSServiceEnabled(!smsServiceEnabled);
            setOpenSMSServiceDialog(false);
            setSMSService(initialSMSService);
        } catch (error) {
            toErrorPage(error);
        }
    }

    const handleEmailService = () => {
        setOpenEmailServiceDialog(!openEmailServiceDialog)
    }

    async function updateEmailServiceStatus() {        
        try {        
            setEmailServiceEnabled(!emailServiceEnabled);
            setOpenEmailServiceDialog(false);
        } catch (error) {
            toErrorPage(error);
        }
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setPreApplicationQuestion((values: any) => ({ ...values, [name]: value }));
        if(name === "smsContactNumber") {
            setContactNumber(value);
            validateInput(name, value); 
        }
        if(validation[name] && validation[name]!.invalid) {
            validateInput(name, value);   
        }
        if(smsValidation[name] && smsValidation[name]!.invalid) {
            validateInput(name, value);   
        }
    }

    const handleSMSChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setSMSService((values: any) => ({ ...values, [name]: value }));
        if(smsValidation[name] && smsValidation[name]!.invalid) {
            validateInput(name, value);   
        }
    }
    
    const handleAddButtonClick = (event: React.FormEvent<HTMLFormElement>) => { 
        event.preventDefault();       
        setShowAddQuestion(prevState => !prevState);
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    const handleDiscardClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault(); 
        setShowAddQuestion(prevState => !prevState);
        setPreApplicationQuestion(initialQuestion);
        setSelectedQuestionType("");
        setValidation(initialValidation);
    }

    const handleEditDiscardClick = (event: React.MouseEvent<SVGSVGElement>) => {
        event.preventDefault();
        setShowAddQuestion(prevState => !prevState);
        setEditEnable(false);
        setPreApplicationQuestion(initialQuestion);
        setValidation(initialValidation);
        setSelectedQuestionType("");
    }

    const handleDeleteClick = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setOpenSingleDeleteDialog(true);
    }

    const deleteSelectedQuetions = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setOpenBulkDeleteDialog(true);
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    const handleFrequentlyQuestions = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if(sampleQuestions.length === 0) 
            getPreApplicationSampleQuestionsList();
        setOpenFrequentlyQuestions(!openFrequentlyQuestions);
    }
    
    const filteredSampleQuestions = sampleQuestions.filter(q =>
        q.question.toLowerCase().includes(searchQuestions.toLowerCase())
    )

    const addInterviewDate = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if(interviewDate === "") return;

        const [year, month, day] = interviewDate.split("-");
        const formattedDate = `${day}/${month}/${year}`;
        setSMSService(prevState => {
            if(prevState.interviewDates.includes(formattedDate)) {
                openAlert("error", "This interview date has already been added."); 
                return prevState;
            }
            return {
                ...prevState,
                interviewDates: [...prevState.interviewDates, formattedDate]
            };
        });
        setInterviewDate("");
    }

    const addLocation = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if(currentLocation != "" && contactNumber != "") {
            if(validatePhoneNumber(contactNumber)) {
                setSMSService(prevState => ({
                    ...prevState,
                    branches: [...prevState.branches, { branchName: currentLocation, phoneNumber: contactNumber }]
                }));
                setCurrentLocation("");
                setContactNumber("");
            }
            else {
                setContactNumberValidation({valid: false, invalid: true, invalidMessage: "Invalid Phone Number"});
            }    
        }
    }
    
    const deleteInterviwDate = (index: number) => {
        setSMSService(prevState => ({
            ...prevState,
            interviewDates: prevState.interviewDates.filter((_, i) => i !== index)
        }));
    }

    const deleteLocation = (index: number) => {
        setSMSService(prevState => ({
            ...prevState,
            branches: prevState.branches.filter((_, i) => i !== index)
        }));
    }

    const addAnswer = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault(); 
        if(currentAnswer != "") {
            setPreApplicationQuestion(prevState => ({
                ...prevState,
                answers: [...prevState.answers, { answer: currentAnswer }]
            }));
            setCurrentAnswer("");
            if(validation["answers"] && validation["answers"]!.invalid) {
                validateInput("answers", currentAnswer);   
            }
        } 
    }

    const handleDeleteAnswer = (anwerIndex: number) => {
        setPreApplicationQuestion(prev => ({
            ...prev,
            answers: prev.answers.filter((_, index) => index !== anwerIndex)
        }));
    }

    const cancelSingleQuestionDelete = () => {
        setOpenSingleDeleteDialog(false);    
    }

    const confirmSingleQuestionDelete = () => {
        setOpenSingleDeleteDialog(false);
        deleteQuestion();
    }

    const cancelBulkQuestionsDelete = () => {
        setOpenBulkDeleteDialog(false);    
    }

    const confirmBulkQuestionsDelete = () => {
        setOpenBulkDeleteDialog(false);
        deleteSelectedQuestions();
    }
    
    const handleQusetionEdit = (question: PreApplicationQuestionModel, index: number) => { 
        const questionType = questionTypeLabels[question.questionType] || "";
        setPreApplicationQuestion({
            jobQuestionId: question.jobQuestionId,
            jobId: jobId,
            question: question.question,                        
            questionType: question.questionType,
            type: questionType,
            answers: question.answers,
            deleted: question.deleted
        });
        setEditQuestionIndex(index);
        setEditEnable(true);
        setShowAddQuestion(true);
        setSelectedQuestionType(questionType);
        topRef.current?.scrollIntoView({ behavior: 'smooth' });    
    }

    const validateInput = (name: string, value: string) => { 
        if(name === "question") {            
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Question is required"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "answers") {            
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "message") {            
            if(value === "") {
                setSMSValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "SMS Message is required"}}));
            } 
            else {
                setSMSValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "smsContactNumber") {
            if(value === "") {
                setContactNumberValidation({valid: true, invalid: false});
            }         
            else if(!validatePhoneNumber(value)) {
                setContactNumberValidation({valid: false, invalid: true, invalidMessage: "Invalid Phone Number"});
            } 
            else {
                setContactNumberValidation({valid: true, invalid: false});
            }
        }
    }

    const validateSMSForm = (): number => {
        let formValidation: SMSModelValidation = {
            message: {valid: true}
        }
        if(!smsService.message) {
            formValidation.message = {valid: false, invalid: true, invalidMessage: "SMS message is required"};
        }
        setSMSValidation(formValidation);       
        const validInputCount = Object.keys(formValidation).filter(key => formValidation[key].valid).length;
        return validInputCount; 
    }

    const validateQuestionForm = (): number => {
        let formValidation: PreApplicationQuestionValidation = {
            question: {valid: true},
            answers: {valid: true}
        } 
        if(preApplicationQuestion.question === "" || preApplicationQuestion.question === undefined) { 
            formValidation.question = {valid: false, invalid: true, invalidMessage: "Question is required"};
        } 
        if(preApplicationQuestion.answers.length == 0 && preApplicationQuestion.type != "Open") {
            formValidation.answers = {valid: false, invalid: true};
        }
        setValidation(formValidation);       
        const validInputCount = Object.keys(formValidation).filter(key => formValidation[key].valid).length;
        return validInputCount; 
    }

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        e.dataTransfer.setData("text/plain", index.toString());
        setDragIndex(index);
    }

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        e.preventDefault();
        setDragOverIndex(index);
    }

    const handleDragLeave = () => {
        setDragOverIndex(null);
    }

    const sanitizeQuotes = (input: string): string =>
        input.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");

    const handleCopyTag = async (
        raw: string,
        openAlert: (type: "success" | "error", message: string) => void
      ) => {
        const text = sanitizeQuotes(raw);

        try {
          if(navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
          } 
          else {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.style.position = "fixed";
            textArea.focus();
            textArea.select();
            const success = document.execCommand("copy");
            document.body.removeChild(textArea);
            if (!success) throw new Error("Copy fallback failed");
          }
          openAlert("success", "Copied!");
        } catch (err: any) {
          openAlert("error", err.message || "Copy failed");
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        e.preventDefault();
        const fromIndex = parseInt(e.dataTransfer.getData("text/plain"));
        if(fromIndex !== index) {
            const newList = [...questionsList];
            const [movedItem] = newList.splice(fromIndex, 1);
            newList.splice(index, 0, movedItem);
            setQuestionsList(newList);
        }
        setDragIndex(null);
        setDragOverIndex(null);
    }

    const handleDragEnd = () => {
        setDragIndex(null);
        setDragOverIndex(null);
    }

    const handleSelectAll = () => {
        const selectableQuestions = questionsList.filter(q => !q.isDefault);
    
        if(selectedQuestionIds.length === selectableQuestions.length) {
            setSelectedQuestionIds([]);
            setAllSelected(false);
        } else {
            setSelectedQuestionIds(selectableQuestions.map(q => q.jobQuestionId));
            setAllSelected(true);
        }
    }

    const handleToggleSelect = (questionId: number) => {        
        if(selectedQuestionIds.includes(questionId)) {            
            setSelectedQuestionIds(prev => prev.filter(id => id !== questionId));
            setAllSelected(false);
        } else {
            const selectableQuestionLength = questionsList.filter(q => !q.isDefault).length;
            if(selectableQuestionLength === selectedQuestionIds.length + 1)
                setAllSelected(true);
            setSelectedQuestionIds(prev => [...prev, questionId]);
        }
    }

    async function deleteSelectedQuestions() {
        if (selectedQuestionIds.length === 0) return;
        
        const updatedList = questionsList.filter(question => !selectedQuestionIds.includes(question.jobQuestionId));
        setUpdatedQuestionList(updatedList);
        setDeletedQuestionIds(prev => [...prev, ...selectedQuestionIds]);
        setQuestionsList(updatedList);
        setSelectedQuestionIds([]);
        setAllSelected(false);
        openAlert("success", "The questions are deleted successfully.");        
    }

    async function addPreApplicationSampleQuestionToJob(question: SampleQuestion) {  
        setSampleQuestionIds(prev => [...prev, question.sampleJobQuestionId]);
        setQuestionsList(prev => [
            ...prev,
            {
                jobQuestionId: Math.floor(Math.random() * -1000),
                jobId: jobId,                        
                question: question.question,                        
                questionType: question.questionTypeId,
                sampleJobQuestionId: question.sampleJobQuestionId,
                answers: question.jobQuestionAnswers.map(ans => ({ 
                    answer: ans.answer 
                })),
                deleted: false
            }
        ]);  
        openAlert("success", "The question is added successfully");     
    }

    async function addPreApplicationQuestionToJob(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();     
        let validInputCount = validateQuestionForm();
        if(validInputCount === 2) {

            const tempId = Math.floor(Math.random() * -1000); 

            const newQuestion = {
                ...preApplicationQuestion,
                jobId: jobId,
                jobQuestionId: tempId,                         
                deleted: false
            };

            setQuestionsList(prev => [...prev, newQuestion]);
            setUpdatedQuestionList(prev => [...prev, newQuestion]);
            setPreApplicationQuestion(initialQuestion);
            setShowAddQuestion(false);
            setSelectedQuestionType("");
            openAlert("success", "The question is added successfully");
        }
        else {
            openAlert("error", "Required fields are missing"); 
            setLoading(false);
        }
    }

    async function updateQuestion(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = validateQuestionForm();
        if(validInputCount == 2) {
            setQuestionsList(prev => 
                prev.map((item, index) => index === editQuestionIndex ? preApplicationQuestion : item)
            );
            
            const originalQuestion = questionsList[editQuestionIndex];
            const isSampleQuestion = originalQuestion?.sampleJobQuestionId != null 
                && sampleQuestionIds.includes(originalQuestion.sampleJobQuestionId);

            if (isSampleQuestion) {
                setSampleQuestionIds(prev => prev.filter(id => id !== originalQuestion.sampleJobQuestionId));
            }

            const existsInUpdatedList = updatedQuestionList.some(q => q.jobQuestionId === preApplicationQuestion.jobQuestionId);
            if (existsInUpdatedList) {
                setUpdatedQuestionList(prev => prev.map(q => 
                    q.jobQuestionId === preApplicationQuestion.jobQuestionId ? preApplicationQuestion : q
                ));
            } 
            else {
                setUpdatedQuestionList(prev => [...prev, preApplicationQuestion]);
            }
            setEditEnable(false);
            setShowAddQuestion(false);
            setPreApplicationQuestion(initialQuestion);
            openAlert("success", "The question is updated successfully");
        }
        else {
            openAlert("error", "Required Fields are Missing");
        }    
    }

    async function deleteQuestion(event?: React.FormEvent<HTMLFormElement>) {
        event?.preventDefault();
        const idToDelete = preApplicationQuestion.jobQuestionId;
        if(idToDelete > 0) 
            setDeletedQuestionIds(prev => [...prev, idToDelete]);
        
        setUpdatedQuestionList(prev => prev.filter(q => q.jobQuestionId !== idToDelete));
        const updatedList = questionsList.filter(question => question.jobQuestionId !== preApplicationQuestion.jobQuestionId);
        setQuestionsList(updatedList);
        setEditEnable(false);
        setShowAddQuestion(false);
        setPreApplicationQuestion(initialQuestion);
        openAlert("success", "The question is deleted successfully.");
    }

    async function handlSMSSaveClick(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = validateSMSForm();
        if(validInputCount === 1) {
            setSMSService(smsService);
            openAlert("success", "SMS details added successfully");
                    
            if(smsService.interviewDates && smsService.interviewDates.length > 0) {

                const newInterviewDateQuestion: PreApplicationQuestionModel = {
                    jobQuestionId: Math.floor(Math.random() * -1000),
                    jobId: jobId,
                    question: "Select Preferred Interview Date",
                    questionType: 3,
                    answers: smsService.interviewDates.map(date => ({ answer: date })),
                    deleted: false
                };

                setQuestionsList(prev => {
                    const filtered = prev.filter(
                        q => q.question !== "Select Preferred Interview Date"
                    );
                    return [...filtered, newInterviewDateQuestion];
                });

            }
            else {
                setQuestionsList(prev => prev.filter(q => q.question !== "Select Preferred Interview Date"));
            }
            if(smsService.branches && smsService.branches.length > 0) {

                const newInterviewDateQuestion: PreApplicationQuestionModel = {
                    jobQuestionId: Math.floor(Math.random() * -1000),
                    jobId: jobId,
                    question: "Select Preferred Interview Location",
                    questionType: 3,
                    answers: smsService.branches.map(({ branchName }) => ({ answer: branchName })),
                    deleted: false,
                };

                setQuestionsList(prev => {
                    const filtered = prev.filter(
                        q => q.question !== "Select Preferred Interview Location"
                    );
                    return [...filtered, newInterviewDateQuestion];
                });
            }
            else {
                setQuestionsList(prev => prev.filter(q => q.question !== "Select Preferred Interview Location"));
            }
        }
        else {
            openAlert("error", "Required fields are missing");
            setLoading(false);
        }     
    }

    const saveTab = async (): Promise<{ data: VacancyEditResponse } | null> => {
        const updatedModel: VacancyEditResponse = {
            jobId: jobId!,
            recruiterId: recruiter.recruiterId,
            sampleQuestionIds: sampleQuestionIds,
            deletedQuestionIds: deletedQuestionIds,
            updatedPreApplicationQuestionList: updatedQuestionList,
            smsService: smsService,
            smsServiceStatus: smsServiceEnabled,
            emailServiceStatus: emailServiceEnabled,
            preApplicationQuestionList: questionsList
        };

        props.onUpdateSummary({
            service: (emailServiceEnabled || smsServiceEnabled)
        });
        props.onSave(updatedModel);

        return { data: updatedModel };
    };

    useImperativeHandle(ref, () => ({ saveTab }));

    return(
        <div style={styles.mainWrapper}>           
            {isMobile ? <div style={styles.subTopicWrapper.topic}>{"CV Less"}</div>
            : <div style={styles.subTopicWrapper}>
                <div style={styles.subTopicWrapper.topic}>{"CV Less"}</div>
            </div>}
            {loading ? <VacancyQuestionLoader/> : <form style={styles.form}>
                <div style={styles.serviceMainWrapper}>
                    <div style={styles.serviceWrapper}>
                        <div style={styles.serviceText}>
                            <span style={styles.Topic}>{"SMS Service"}</span>
                            <span style={styles.subTopic}>{"Activate SMS Service"}</span>
                        </div>
                        <Switch styles={styles.tabSwitch} checked={smsServiceEnabled} handleClick={handleSMSService}/>
                    </div>
                    
                </div>
                {smsServiceEnabled && <div style={styles.smsServiceMainWrapper}>
                    <span style={styles.smsServiceTittle}>{"SMS Service"}</span>
                    <div style={styles.textAreaWrapper}>
                        <label style={styles.label}>{"SMS Message"}</label>
                        <TextAreaInput                
                            name={"message"}
                            value={smsService.message} 
                            styles={styles.textAreaInput}
                            handleChange={handleSMSChange}
                            validation={smsValidation.message}                                           
                        /> 
                    </div> 
                    <span style={styles.showTags} onClick={()=> setshowTags(true)}>{"Show Tags"}</span>
                    <div style={styles.dateWrapper}>
                        <div style={styles.dateInputWrapper}>
                            <label style={styles.label}>{"Interview Dates"}</label>
                            <input
                                type={"date"}
                                min={new Date().toISOString().split("T")[0]}
                                style={styles.dateInput}
                                name={"interviewDate"}
                                placeholder={"DD/MM/YYYY"}
                                value={interviewDate}
                                onChange={(e) => setInterviewDate(e.target.value)}
                            />
                        </div>
                        <Button style={styles.interviewDateAddButton} onClick={addInterviewDate}>{"Add"}</Button>
                    </div>
                    {smsService.interviewDates.map((interviewDate, index) => (
                        <div style={styles.locationAnswerWrapper} key={index}>
                            <span style={styles.locationAnswer}>{"Interview Date"}</span>
                            <span style={styles.locationAnswer}>{interviewDate}</span>
                            <Delete style={styles.deleteIcon} onClick={() => deleteInterviwDate(index)}/>
                        </div>
                    ))}
                    <div style={styles.locationWrapper}>
                        <div style={styles.locationInputWrapper}>
                            <label style={styles.label}>{"Location"}</label>
                            <TextInput
                                inputType={"text"}
                                value={currentLocation}
                                styles={styles.textInput}
                                handleChange={(event) => setCurrentLocation(event!.target.value)}
                            /> 
                        </div>
                        <div style={styles.locationInputWrapper}>
                            <label style={styles.label}>{"Contact Number"}</label>
                            <TextInput
                                inputType={"text"}
                                name={"smsContactNumber"}
                                value={contactNumber}
                                styles={styles.textInput}
                                handleChange={handleInputChange}
                                validation={contactNumberValidation}   
                            /> 
                        </div>
                        <Button style={styles.locationAddButton} onClick={addLocation}>{"Add"}</Button>
                    </div>
                    {smsService.branches.map((location, index) => (
                        <div style={styles.locationAnswerWrapper} key={index}>
                        <div style={styles.answerGruop}>
                            <span style={styles.locationAnswer}>{location.branchName}</span>
                            <span style={styles.locationAnswer}>{location.phoneNumber}</span>
                        </div>
                            <Delete style={styles.deleteIcon} onClick={() => deleteLocation(index)}/>
                        </div>
                     ))}
                    <Button style={styles.smsSave} onClick={handlSMSSaveClick}>{"SAVE"}</Button>
                </div>}
                <div ref={topRef}></div>
                <div style={styles.addNewQuestionWrapper}>
                    {!isMobile && "Questions"}
                    <Button style={styles.frequenltyQuestionButton} onClick={handleFrequentlyQuestions}>{"FREQUENTLY USED QUESTIONS"}</Button>
                    <Button style={styles.addNewQuestionButton} onClick={handleAddButtonClick}>{"ADD NEW QUESTION"}
                        <Plus style={styles.plusIcon}/>
                    </Button>
                </div>
                {showAddQuestion  && <div style={styles.addNewquestion} >
                    <div style={styles.tittleWithIcon}>                        
                        {editEnable ? <div style={styles.qusetionTittle}>{`Question ${editQuestionIndex} (Edit)`}</div>
                        : <div style={styles.qusetionTittle}>{"New Question"}</div>}
                        <DroupUp style={styles.groupIcon.icon} onClick={handleEditDiscardClick}/>
                    </div>
                    <div style={styles.groupWrapper}>
                        <div style={styles.rightMargin}>
                            <Selector
                                label={"Question Type"} 
                                placeHolder={"Select one"}
                                items={jobQuestionTypes}
                                name= {"type"}
                                value={preApplicationQuestion.type}
                                onSelectItem={handleSelectItems}
                                styles={styles.selector}
                            />
                        </div>
                        <div style={{...styles.inputWrapper, ...styles.leftMargin}}>
                            <label style={styles.questionLabel}>{"Question"}</label>
                            <TextInput
                                inputType={"text"}
                                name={"question"}
                                value={preApplicationQuestion.question}
                                styles={styles.textInput}
                                handleChange={handleInputChange}
                                validation={validation.question}                    
                            /> 
                        </div>
                    </div>
                    <div style={styles.answerWrapper}>                        
                        {selectedQuestionType == "Single Choice" && <div style={styles.addSingleChoiceAnswer}>
                            <div>
                                <label style={styles.questionLabel}>{"Answer"}</label>
                                <div style={{...styles.singleAnswerWrapper, ...(validation.answers.invalid && styles.singleAnswerWrapper.invalid)}}>
                                    <input 
                                        type={"text"} 
                                        placeholder={"Answer"} 
                                        value={currentAnswer} 
                                        onChange={(e) => setCurrentAnswer(e.target.value)} 
                                        style={{...styles.singleAnswer, ...(validation.answers.invalid && styles.singleAnswer.invalid)}}
                                    />
                                </div>
                                <Button style={styles.KnockoutAddButton} onClick={addAnswer}>{"ADD ANSWER"}</Button>
                            </div>
                            <div style={styles.knockoutAnswersWrapper}>
                                <div style={styles.answersLabelWrapper}>
                                    <label style={styles.questionLabel}>{"Answers"}</label>
                                    <label style={styles.questionLabel}>{"Action"}</label>
                                </div>                                
                                {preApplicationQuestion.answers.map((answer, index)=> 
                                    <div style={styles.answerAndScoreWrapper}>
                                        <label style={styles.answerAndScoreWrapper.answer}>{answer.answer}</label>
                                        <div style={styles.textWithIcon}><Delete style={styles.deleteIcon} onClick={() => handleDeleteAnswer(index)}/></div>
                                    </div> 
                                )}                                    
                                <hr/>
                            </div>
                        </div>}
                    </div>                    
                    {editEnable ? <div style={styles.buttonWrapper}>
                        <div style={styles.buttonWrapper.group}>
                            <div style={styles.buttonWrapper.topRow}>
                                <Button style={styles.buttonWrapper.update} onClick={updateQuestion}>{"UPDATE"}</Button>
                            </div>
                            <div style={styles.buttonWrapper.bottomRow}>
                                <Button style={styles.buttonWrapper.saveDraft} onClick={handleEditDiscardClick}>{"DISCARD"}</Button>
                                <Button style={styles.buttonWrapper.delete} onClick={handleDeleteClick}>{"DELETE"}</Button>
                            </div>
                        </div>                        
                    </div>
                    : <div style={styles.buttonWrapper}>
                        <Button style={styles.buttonWrapper.submit} onClick={addPreApplicationQuestionToJob}>{"ADD QUESTION"}</Button>
                        <Button style={styles.buttonWrapper.saveDraft} onClick={handleDiscardClick}>{"DISCARD"}</Button>
                    </div>}
                </div>}
                <div>
                    <div style={styles.checkBoxWrapper}>
                        {questionsList.length > 0 && <CheckboxInput name="selectAll" value={allSelected} handleChange={handleSelectAll}>
                            <span  style={styles.checkBoxText}>{"Select all"}</span>
                        </CheckboxInput>}                   
                    </div>
                    {questionsList.map((question, index) => {
                        const questionType = questionTypeLabels[question.questionType];
                        const isChecked = selectedQuestionIds.includes(question.jobQuestionId);
                        const isLengthy = index === (questionsList.length - 3);
                        return (
                            <div
                                key={question.jobQuestionId}
                                draggable
                                ref={isLengthy ? lastQuestionRef : null}
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                                style={styles.questions}
                                onClick={
                                    question.isDefault
                                        ? () => setOpenDefaultQuestionsDialog(true)
                                        : undefined
                                }
                            >
                            <div style={styles.labelWithIcon}>
                                <div style={styles.checkBoxGroup}>
                                    {!question.isDefault ? <CheckboxInput name={`question-${question.jobQuestionId}`} value={isChecked} handleChange={() => handleToggleSelect(question.jobQuestionId)}>
                                        <div style={styles.qusetionTittle}>{"Question "}{String(index + 1).padStart(2, "0")}</div>
                                    </CheckboxInput> 
                                    : <div style={styles.qusetionTittle}>{"Question "}{String(index + 1).padStart(2, "0")}</div>}
                                </div>
                                <div style={styles.iconGroup}>
                                    {!question.isDefault && <Edit style={styles.groupIcon.icon} onClick={() => handleQusetionEdit(question, index)}/>}
                                    <Menu style={styles.menuIcon}/>
                                </div>
                            </div>
                            <div style={styles.insideQuestion}>
                                <div style={styles.questionType}>{questionType}</div>
                                <div style={styles.questionTextWrapper}>
                                    {question.question}
                                    {question.questionType !== 5 && (
                                    <div style={styles.questionAnswerWrapper}>
                                        {question.answers.map((answer, index)=> (
                                            <div key={index} style={styles.answers}>
                                                <span>{answer.answer}</span>
                                            </div>
                                        ))}
                                    </div>
                                    )}
                                </div>
                            </div>
                        </div>)
                    })}
                </div>
                {questionsList.length > 0 && <div style={styles.singleButton}>
                    {selectedQuestionIds.length === 0 && <Button style={styles.singleAddNewQuestionButton} onClick={handleAddButtonClick}>{"ADD NEW QUESTION"}
                        <Plus style={styles.plusIcon}/>
                    </Button>}
                    {selectedQuestionIds.length > 0 && <Button style={styles.selectedQuetionsDeleteButton} onClick={deleteSelectedQuetions}>{"DELETE SELECTED QUESTIONS"}
                    </Button>}
                </div>}
            </form>}
            <PopUpWindow open={(openFrequentlyQuestions && !loading)} styles={styles.questionPopup} toggle={()=> setOpenFrequentlyQuestions(!openFrequentlyQuestions)} closeCircle={true}>
                <div style={styles.popUpForm}>
                    <input type={"text"} placeholder={"Search for sample questions..."} style={styles.searchBar} onChange={(e) => setSearchQuestions(e.target.value)}/>
                    <div style={styles.questionListWrapper}>
                        {filteredSampleQuestions.map((question, index)=> { 
                            const qusetionType = questionTypeLabels[question.questionTypeId];
                            return(
                                <div key={index} style={styles.popUpQuestionWrapper}>
                                    <div style={styles.questionAndAnswer}>
                                        <div>{question.question}</div>
                                        {question.jobQuestionAnswers.map((answer, index) => (
                                            <div key={index} style={styles.sampleAnswers}>
                                                <span>{answer.answer}</span>{" - "}<span>{answer.score}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div style={styles.questionTypeAndButton}>
                                        <div>{qusetionType}</div>
                                        <Button style={styles.buttonsWrapper.add} onClick={() => addPreApplicationSampleQuestionToJob(question)}>{"Add"}</Button>
                                    </div>
                                </div> 
                            )
                        })}                   
                    </div> 
                    <div style={styles.popUpButtonWrapper}>
                        <Button style={styles.buttonsWrapper.ok} onClick={() => setOpenFrequentlyQuestions(!openFrequentlyQuestions)}>{"OK"}</Button> 
                    </div>         
                </div>
            </PopUpWindow>
            <PopUpWindow styles={styles.deleteWarningDialog} open={openSingleDeleteDialog} toggle={()=> setOpenSingleDeleteDialog(!openSingleDeleteDialog)} closeCircle={true}>
                <h1 style={styles.deleteDialogTitle}>{"Are you sure?"}</h1>
                <p style={styles.deleteDialogMessage}>{"This question will be deleted permanently."}</p>
                <div className="center-content" style={styles.deleteButtons}>
                    <Button style={{...styles.deleteButton, ...styles.deleteButton.red}} onClick={confirmSingleQuestionDelete}>{"YES"}</Button>
                    <Button style={{...styles.deleteButton, ...styles.deleteButton.blue}} onClick={cancelSingleQuestionDelete}>{"NO"}</Button> 
                </div>
            </PopUpWindow>
            <PopUpWindow styles={styles.deleteWarningDialog} open={openBulkDeleteDialog} toggle={()=> setOpenBulkDeleteDialog(!openBulkDeleteDialog)} closeCircle={true}>
                <h1 style={styles.deleteDialogTitle}>{"Are you sure?"}</h1>
                <p style={styles.deleteDialogMessage}>{"Selected questions will be deleted permanently."}</p>
                <div className="center-content" style={styles.deleteButtons}>             
                    <Button style={{...styles.deleteButton, ...styles.deleteButton.red}} onClick={confirmBulkQuestionsDelete}>{"YES"}</Button>
                    <Button style={{...styles.deleteButton, ...styles.deleteButton.blue}} onClick={cancelBulkQuestionsDelete}>{"NO"}</Button> 
                </div>
            </PopUpWindow>
            <PopUpWindow styles={styles.smsServiceDialog} open={openSMSServiceDialog} toggle={handleSMSService} closeCircle={true}>
                <h1 style={styles.deleteDialogTitle}>{"Please contact your account manager to enable SMS Service."}</h1>
            </PopUpWindow>
            <PopUpWindow styles={styles.defaultQuestionDialog} open={openDefaultQuestionsDialog} toggle={()=> setOpenDefaultQuestionsDialog(false)} closeCircle={true}>
                <h1 style={styles.serviceMessageText}>{"Default questions cannot be edited."}</h1>
            </PopUpWindow>
            <PopUpWindow styles={styles.showTagWrapper} open={showTags} toggle={()=> setshowTags(!showTags)} closeCircle={true}>
                <div style={styles.tagTextAndIconWrapper}>
                    <div style={styles.tagTextAndIcon}>{"Candidate Name - <CandidateName>"}<Duplicate style={styles.duplicateIcon} onClick={() => handleCopyTag("Candidate Name - <CandidateName>", openAlert)}/></div>
                    <div style={styles.tagTextAndIcon}>{"Company Name - <CompanyName>"}<Duplicate style={styles.duplicateIcon} onClick={() => handleCopyTag("Company Name - <CompanyName>", openAlert)}/></div>
                    <div style={styles.tagTextAndIcon}>{"Job Title - <JobTitle>"}<Duplicate style={styles.duplicateIcon} onClick={() => handleCopyTag("Job Title - <JobTitle>", openAlert)}/></div>
                    <div style={styles.tagTextAndIcon}>{"Interview Date - <InterviewDate>"}<Duplicate style={styles.duplicateIcon} onClick={() => handleCopyTag("Interview Date - <InterviewDate>", openAlert)}/></div>
                    <div style={styles.tagTextAndIcon}>{"Interview Location - <InterviewLocation>"}<Duplicate style={styles.duplicateIcon} onClick={() => handleCopyTag("Interview Location - <InterviewLocation>", openAlert)}/></div>
                    <div style={styles.tagTextAndIcon}>{"Contact Number - <ContactNumber>"}<Duplicate style={styles.duplicateIcon} onClick={() => handleCopyTag("Contact Number - <ContactNumber>", openAlert)}/></div>
                </div>
            </PopUpWindow>
            <Alert/>
        </div>
    )
})